import { type DynamicColumn } from '@/lib/csv-box';
import { PricingMode } from '@/lib/graphql/generated/graphql';

/**
 * Aplatit les valeurs des colonnes dynamiques (renvoyées par CSVBox dans
 * `_dynamic_data`) au niveau racine de chaque ligne, pour qu'elles soient
 * incluses dans le CSV envoyé au backend (sinon elles seraient sérialisées
 * comme « [object Object] »).
 */
export function flattenDynamicData(rows: Record<string, unknown>[]) {
  return rows.map((row) => {
    const dynamic = row._dynamic_data as Record<string, unknown> | undefined;
    if (!dynamic) {
      return row;
    }
    const extended = { ...row, ...dynamic };
    delete extended._dynamic_data;
    return extended;
  });
}

/** Merge selected e-merch columns from _unmapped_data into each row. */
export function mergeUnmappedColumns(
  rows: Record<string, unknown>[],
  columns: string[]
) {
  return rows.map((row) => {
    const unmapped = row._unmapped_data as Record<string, unknown> | undefined;
    const extended = { ...row };
    delete extended._unmapped_data;
    for (const col of columns) {
      extended[col] = unmapped?.[col] ?? '';
    }
    return extended;
  });
}

/**
 * Colonnes à SAISIR exposées dynamiquement à CSVBox selon le mode de calcul
 * (cf. help.csvbox.io/advanced-installation/dynamic-columns).
 * Le `column_name` reprend le nom canonique des colonnes (cf. mapping.ts) :
 * c'est lui qui fait foi côté backend (pas de doublon des colonnes historiques).
 *
 * D'après les règles de dérivation backend, dans les DEUX modes le prix public
 * et le prix Choose sont saisis, et le discount est dérivé. Le discriminateur :
 * - MarginFixed      : on saisit la marge (discount + rétrocession dérivés).
 * - BuyingPriceFixed : on saisit la rétrocession (discount + marge dérivés).
 *
 * Nuance backend : `discount` ↔ `choosePrice` sont interchangeables. Le backend
 * attend listPrice + (discount OU choosePrice) ; celui fourni prime, l'autre est
 * dérivé. Parti pris : on expose le flux « choosePrice en entrée » (discount
 * dérivé) ; repasser sur « Discount (%) recommandé » suffirait à inverser le sens.
 */
const matchingKeywords = (keywords: string[]) => ({
  matching_keywords: keywords.join(', '),
  // CSVBox currently gates dynamic `matching_keywords` behind its unrelated
  // system-keywords option. Its regular `keywords` path is always evaluated.
  keywords: keywords.map((value) => ({ value })),
});

export const PRICE_COLUMNS = {
  choosePrice: {
    column_name: 'Prix choose ttc recommandé',
    required: false,
    ...matchingKeywords([
      'Prix Choose TTC',
      'Prix Choose',
      'Choose Price',
      'Prix Retail TTC Choose',
      'Prix Choose Final',
      'Prix Psychologique',
      'Contre-proposition',
      'Contre proposition',
    ]),
  },
  retro: {
    column_name: 'Prix de rétrocession ht',
    required: false,
    ...matchingKeywords([
      'Prix de Rétrocession HT',
      'Prix de rétro',
      'Buying Price',
      'Prix de rétrocession',
      'PRIX RETROCESSION HT',
      'B2B Price',
      'Prix B2B HT',
      'BtoB Price',
    ]),
  },
  margin: {
    column_name: 'Marge',
    required: false,
    ...matchingKeywords([
      'Marge',
      'Commission',
      'Margin',
      'Marge Choose',
      'Choose margin',
    ]),
  },
} satisfies Record<string, DynamicColumn>;

export const dynamicColumnsForPricingMode = (
  mode: PricingMode
): DynamicColumn[] => {
  const { choosePrice, retro, margin } = PRICE_COLUMNS;
  return mode === PricingMode.BuyingPriceFixed
    ? [choosePrice, retro]
    : [choosePrice, margin];
};

/** Nettoie un nom de fichier : retire accents et caractères non sûrs. */
export const sanitizeFilename = (filename: string) => {
  return filename
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
    .replace(/[^\w\s.-]/g, ''); // Garder seulement alphanumérique, espaces, points et tirets
};
